import axios from 'axios';
import {
  clearStoredAuth,
  getRefreshStorage,
  getStoredAccessToken
} from './authStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Single shared axios instance for the entire application.
 * All domain clients (auth, user, permission, feedback, inbox, mekak, dev)
 * use this instance. Interceptors are registered once here.
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    Accept: 'application/json',
  },
});

// ─── Request interceptor: attach bearer token ───
apiClient.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
    const headers = config.headers as any;
    if (typeof headers.delete === 'function') {
      headers.delete('Content-Type');
      headers.delete('content-type');
    } else {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
  }
  const token = getStoredAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response interceptor: unified error handling ───
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 403 — insufficient permissions
    if (error.response?.status === 403) {
      const { message } = await import('antd');
      message.error('權限不足，請聯繫管理員');
      return Promise.reject(error);
    }

    // 429 — rate limited
    if (error.response?.status === 429) {
      error.message = '請求頻率過高，請稍後再試';
      return Promise.reject(error);
    }

    // 401 from the login endpoint itself = wrong credentials, NOT an expired
    // session. Reject as-is (no refresh, no toast, no redirect, no storage
    // clear) so the login page can show the backend message. Precise match:
    // '/auth/login-attempts' must still go through the normal flow below.
    if (
      error.response?.status === 401 &&
      /\/auth\/login(?:\?|$)/.test(originalRequest?.url || '')
    ) {
      return Promise.reject(error);
    }

    // 401 — try token refresh (once)
    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !originalRequest?.url?.includes('/auth/refresh')
    ) {
      originalRequest._retry = true;

      try {
        const refreshStorage = getRefreshStorage();
        const refreshToken = refreshStorage?.getItem('refreshToken');

        if (refreshToken && refreshStorage) {
          const { authApi } = await import('../api/userApi');
          const response = await authApi.refreshToken({ refreshToken });
          const accessToken = response.data.accessToken;

          refreshStorage.setItem('token', accessToken);
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        const { message } = await import('antd');
        message.error('登入已過期，請重新登入');
        clearStoredAuth();

        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);

        return Promise.reject(refreshError);
      }
    }

    // 401 without refresh — redirect to login
    if (error.response?.status === 401) {
      const { message } = await import('antd');
      message.error('登入已過期，請重新登入');
      clearStoredAuth();
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }

    return Promise.reject(error);
  }
);

export { API_BASE_URL };
export default apiClient;
